import { readReceiptApiClient } from 'myNet'

// ===== Request types =====

export interface RrEmailAddress {
  email: string
  display_name?: string | null
}

export interface RrAttachmentItem {
  filename: string
  content_base64: string
  content_type?: string
  content_id?: string | null
}

export interface SendRequest {
  sender: RrEmailAddress
  to?: RrEmailAddress[]
  cc?: RrEmailAddress[]
  bcc?: RrEmailAddress[]
  subject: string
  html_body?: string | null
  text_body?: string | null
  oauth_token?: string | null
  attachments?: RrAttachmentItem[] | null
}

// ===== Response types =====

export interface RecipientResult {
  email: string
  display_name?: string | null
  type?: 'to' | 'cc' | 'bcc'
  receipt_id: string | null
  message_id: string | null
  status: 'sent' | 'failed'
  error?: string | null
}

export interface SendResponse {
  send_id: string
  sent_count: number
  failed_count: number
  results: RecipientResult[]
}

/**
 * `POST /api/read/{receipt_id}` 응답 + `GET /api/read/{receipt_id}/state` 응답.
 * 두 endpoint의 응답 schema는 동일하다 (CLIENT_API_GUIDE.md v2026.04.28-5).
 */
export interface ReadReportResponse {
  status: string
  receipt_id: string
  /** 같은 receipt_id에 이전에도 보고가 있었으면 true. 멱등 판정용. */
  already_reported: boolean
  /** 동일 receipt_id의 최초 보고 시각 (UTC). 첫 호출이면 이번 시각, 아니면 과거 시각 유지. */
  first_reported_at: string | null
  /** 누적 보고 횟수 (이번 호출 포함, app_api source 기준). */
  report_count: number
}

export interface ByTypeCount {
  total: number
  read: number
}

export interface RecipientStatus {
  receipt_id: string
  message_id: string
  sender_email: string
  recipient_email: string
  recipient_type: 'to' | 'cc' | 'bcc'
  recipient_display_name?: string | null
  sender_display_name?: string | null
  subject: string | null
  mail_format: string
  pixel_inserted: boolean
  sent_at: string
  status: 'delivered' | 'read'
  is_proxy_read: boolean
  first_read_at: string | null
  first_read_real_at: string | null
  last_read_at: string | null
  read_count: number
  read_count_real: number
  read_count_proxy: number
}

export interface SendStatusResponse {
  send_id: string
  sender_email: string
  sender_display_name?: string | null
  subject: string | null
  sent_at: string
  total: number
  read: number
  unread: number
  read_percentage: number
  /**
   * by_type: API 응답 예시(CLIENT_API_GUIDE.md v2026.04.28-5)에 to/cc만 포함되는 경우가 있어
   * 모든 key를 optional로 둔다. bcc 키 부재 응답에서도 안전하게 동작.
   */
  by_type: Partial<Record<'to' | 'cc' | 'bcc', ByTypeCount>>
  recipients: RecipientStatus[]
}

interface ApiEnvelope<T> {
  code: number
  message: string
  data: T | null
}

const readReceiptApiHelper = {
  // POST /api/send — 수신확인 메일 발송
  sendMail: async (data: SendRequest): Promise<SendResponse> => {
    const res = await readReceiptApiClient.post('/api/send', data)
    if (res.data != null) {
      const { code, data: payload } = res.data as ApiEnvelope<SendResponse>
      if (code === 200 && payload != null) return payload
      throw new Error(res.data.message ?? 'Failed to send read-receipt mail')
    }
    throw new Error('Failed to send read-receipt mail')
  },

  // GET /api/send/message-id/{message_id} — 발송한 메일의 수신 현황 조회 (발송자 인증 필요)
  getStatusByMessageId: async (messageId: string): Promise<SendStatusResponse> => {
    const path = `/api/send/message-id/${encodeURIComponent(messageId)}`
    const res = await readReceiptApiClient.get(path)
    if (res.data != null) {
      const { code, data: payload } = res.data as ApiEnvelope<SendStatusResponse>
      if (code === 200 && payload != null) return payload
      throw new Error(res.data.message ?? 'Failed to fetch read-receipt status')
    }
    throw new Error('Failed to fetch read-receipt status')
  },

  // POST /api/read/{receipt_id} — 명시적 read 보고 (인증 불필요, 멱등)
  reportRead: async (receiptId: string): Promise<ReadReportResponse> => {
    const path = `/api/read/${encodeURIComponent(receiptId)}`
    const res = await readReceiptApiClient.post(path)
    if (res.data != null) {
      const { code, data: payload } = res.data as ApiEnvelope<ReadReportResponse>
      if (code === 200 && payload != null) return payload
      throw new Error(res.data.message ?? 'Failed to report read receipt')
    }
    throw new Error('Failed to report read receipt')
  },

  // GET /api/read/{receipt_id}/state — 보고 상태 조회 (DB 쓰기 없음, 인증 불필요)
  getReadState: async (receiptId: string): Promise<ReadReportResponse> => {
    const path = `/api/read/${encodeURIComponent(receiptId)}/state`
    const res = await readReceiptApiClient.get(path)
    if (res.data != null) {
      const { code, data: payload } = res.data as ApiEnvelope<ReadReportResponse>
      if (code === 200 && payload != null) return payload
      throw new Error(res.data.message ?? 'Failed to fetch read receipt state')
    }
    throw new Error('Failed to fetch read receipt state')
  },
}

export default readReceiptApiHelper
